import React, { useEffect, useRef, useState } from "react";
import { auth, db } from "@/lib/firebase";
import { doc, onSnapshot } from "firebase/firestore";
import { toast } from "@/components/ui/use-toast";
import { requestFollow, cancelFollowRequest, removeFollowEdge } from "@/data/follow";

type Props = {
  targetUid: string;
  appearance?: "menu" | "profile" | "inline" | "default";
  className?: string;
  // NEW: use for success message like "Now following {username}!"
  username?: string;
};

type FollowStatus = "pending" | "approved";

// Small helper to know when the button is actually on-screen.
function useVisibility<T extends HTMLElement>(threshold = 0): [React.RefObject<T>, boolean] {
  const ref = useRef<T>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setVisible(true);
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);

  return [ref, visible];
}

export default function FollowButton({ targetUid, className, appearance = "menu", username }: Props) {
  const me = auth.currentUser?.uid || null;
  const isSelf = me && me === targetUid;
  const [privacy, setPrivacy] = useState<"public" | "review" | "private">("review");
  const [status, setStatus] = useState<FollowStatus | null>(null);
  const [loading, setLoading] = useState(false);

  const [hostRef, visible] = useVisibility<HTMLDivElement>(0);

  // Subscribe to target privacy immediately (cheap; single doc)
  useEffect(() => {
    const unsub = onSnapshot(doc(db, "users", targetUid), (snap) => {
      const d = snap.data() as any;
      const raw = d?.follow_privacy ?? d?.preferences?.follow_privacy ?? "review";
      const v = String(raw || "review").toLowerCase();
      setPrivacy(v === "public" || v === "private" ? v : "review");
    });
    return () => unsub();
  }, [targetUid]);

  // Subscribe to the follow edge only once the button is visible to the user
  useEffect(() => {
    if (!me || isSelf || !visible) {
      setStatus(null);
      return;
    }
    const unsub = onSnapshot(
      doc(db, "follows", `${me}_${targetUid}`),
      (snap) => {
        if (!snap.exists()) setStatus(null);
        else setStatus((snap.data() as any).status as FollowStatus);
      },
      (err) => {
        if (err?.code !== "permission-denied") {
          console.warn("follow edge listen error:", err);
        }
        setStatus(null);
      }
    );
    return () => unsub();
  }, [me, isSelf, targetUid, visible]);

  // Map states to user-facing text
  const label =
    status === "approved" ? "Following" :
    status === "pending"  ? "Requested" :
    privacy === "private" ? "Private"  :
    "Follow";

  // Styles by appearance + state
  const styles = {
    menu: {
      base: "px-2 py-1 rounded text-xs transition-colors",
      follow: "bg-fuchsia-600 hover:bg-fuchsia-500 text-white",
      approved: "bg-slate-700 hover:bg-slate-600 text-white",
      pending: "bg-slate-700 hover:bg-slate-600 text-white",
      private: "bg-slate-800 text-white opacity-60 cursor-not-allowed",
    },
    profile: {
      base: "px-3 py-1 rounded text-sm border shadow-sm transition-colors",
      follow: "bg-violet-700 hover:bg-violet-600 text-white border-violet-400/30",
      approved: "bg-slate-700 hover:bg-slate-600 text-white border-slate-500/40",
      pending: "bg-slate-700 hover:bg-slate-600 text-white border-slate-500/40",
      private: "bg-slate-800 text-white opacity-60 border-slate-600 cursor-not-allowed",
    },
  } as const;

  const s = styles[appearance];

  async function click(run: () => Promise<unknown>, success?: string) {
    try {
      setLoading(true);
      await run();
      if (success) toast({ description: success });
    } catch (e: any) {
      toast({ description: e?.message ?? "Action failed" });
    } finally {
      setLoading(false);
    }
  }

  async function handleRequestFollow(targetUid: string, username?: string) {
    try {
      const { status } = await requestFollow(targetUid);
      if (status === "approved") {
        toast({
          description: `Now following ${username ? `@${username}` : "this user"}!`,
        });
      } else {
        toast({
          description: "Follow request sent",
        });
      }
    } catch (e: any) {
      const msg =
        e?.message && typeof e.message === "string"
          ? e.message
          : "Could not follow. Try again.";
      toast({ description: msg });
    }
  }

  if (isSelf) {
    return (
      <span className={`text-xs opacity-70 ${className || ""}`} aria-label="This is you">
        You
      </span>
    );
  }

  return (
    <div ref={hostRef} className="inline-block">
      {!me ? (
        <button
          className={`${s.base} ${s.follow} ${className || ""}`}
          aria-label="Sign in to follow users"
          onClick={() => toast({ title: "Sign in to follow users." })}
        >
          Follow
        </button>
      ) : status === "approved" ? (
        <button
          className={`${s.base} ${s.approved} ${className || ""}`}
          aria-label="Unfollow user"
          disabled={loading}
          onClick={() =>
            click(() => removeFollowEdge(`${me}_${targetUid}`), "Unfollowed")
          }
        >
          {loading ? "Working…" : label}
        </button>
      ) : status === "pending" ? (
        <button
          className={`${s.base} ${s.pending} ${className || ""}`}
          aria-label="Cancel follow request"
          disabled={loading}
          onClick={() => click(() => cancelFollowRequest(targetUid), "Request canceled")}
        >
          {loading ? "Working…" : label}
        </button>
      ) : privacy === "private" ? (
        <button
          className={`${s.base} ${s.private} ${className || ""}`}
          aria-label="User profile is private"
          disabled
        >
          {label}
        </button>
      ) : (
        <button
          className={`${s.base} ${s.follow} ${className || ""}`}
          aria-label="Follow user"
          disabled={loading}
          onClick={async () => {
            // ...existing state guards...
            await handleRequestFollow(targetUid, username);
          }}
        >
          {loading ? "Working…" : label}
        </button>
      )}
    </div>
  );
}

